import { useState, useEffect, useRef } from 'react';
import { X, RotateCcw, AlertTriangle, Check, Loader2, Search, Printer } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { ventasService } from '@/services/ventas.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { useReactToPrint } from 'react-to-print';
import { CreditNoteTicket } from '@/components/CreditNoteTicket';

interface ReturnItem {
  productId: string;
  name: string;
  quantity: number;
  maxQuantity: number;
  unitPrice: number;
  selected: boolean;
  returnQuantity: number;
}

interface ReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Si viene, busca esa venta al abrir (evita que el usuario vuelva a teclear el folio) */
  initialFolio?: string;
}

export function ReturnModal({ isOpen, onClose, initialFolio }: ReturnModalProps) {
  const { user } = useAuthStore();
  const { error, errorType, setError, clearError, handleApiError, handleAuthRedirect } = useApiError();

  const [folioSearch, setFolioSearch] = useState('');
  const [sale, setSale] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ReturnItem[]>([]);
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [step, setStep] = useState<'search' | 'items' | 'result'>('search');

  const printRef = useRef<HTMLDivElement>(null);
  const [creditNoteToPrint, setCreditNoteToPrint] = useState<any>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => setCreditNoteToPrint(null),
  });

  useEffect(() => {
    if (creditNoteToPrint) {
      setTimeout(() => { handlePrint(); }, 100);
    }
  }, [creditNoteToPrint, handlePrint]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialFolio?.trim()) {
      setFolioSearch(initialFolio.trim());
      // Buscar con el folio recibido (no depender del setState async)
      (async () => {
        if (!user?.company?.id) return;
        setLoading(true);
        setError(null);
        try {
          const result = await ventasService.findAll({
            companyId: user.company.id,
            search: initialFolio.trim(),
            limit: 1,
          });
          const found = result.data?.[0];
          if (!found) {
            setError('Venta no encontrada con ese folio');
            setLoading(false);
            return;
          }
          if (found.status !== 'COMPLETED') {
            setError('Solo se pueden devolver ventas completadas');
            setLoading(false);
            return;
          }
          // Cargar detalle si la lista no trae items
          let saleData = found;
          if (!found.items?.length) {
            saleData = await ventasService.findById(found.id);
          }
          setSale(saleData);
          const returnableItems = (saleData.items || [])
            .map((item: any) => {
              const maxReturnable = item.quantity - (item.returnedQuantity || 0);
              return {
                productId: item.productId,
                name: item.product?.name || 'Producto',
                quantity: item.quantity,
                maxQuantity: maxReturnable,
                unitPrice: Number(item.unitPrice),
                selected: maxReturnable > 0,
                returnQuantity: maxReturnable > 0 ? maxReturnable : 0,
              };
            })
            .filter((item: any) => item.maxQuantity > 0);

          if ((saleData.items || []).length > 0 && returnableItems.length === 0) {
            setError('Todos los productos de esta venta ya han sido devueltos.');
            setLoading(false);
            return;
          }
          setItems(returnableItems);
          setStep('items');
        } catch (err: any) {
          handleApiError(err);
        }
        setLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialFolio]);

  const handleSearch = async () => {
    if (!user?.company?.id || !folioSearch.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await ventasService.findAll({
        companyId: user.company.id,
        search: folioSearch.trim(),
        limit: 1,
      });
      const found = result.data?.[0];
      if (!found) {
        setError('Venta no encontrada con ese folio');
        setLoading(false);
        return;
      }
      if (found.status !== 'COMPLETED') {
        setError('Solo se pueden devolver ventas completadas');
        setLoading(false);
        return;
      }

      setSale(found);
      const returnableItems = (found.items || [])
          .map((item: any) => {
            const maxReturnable = item.quantity - (item.returnedQuantity || 0);
            return {
              productId: item.productId,
              name: item.product?.name || 'Producto',
              quantity: item.quantity,
              maxQuantity: maxReturnable,
              unitPrice: Number(item.unitPrice),
              selected: false,
              returnQuantity: maxReturnable > 0 ? 1 : 0,
            };
          })
          .filter((item: any) => item.maxQuantity > 0);

      if ((found.items || []).length > 0 && returnableItems.length === 0) {
        setError('Todos los productos de esta venta ya han sido devueltos.');
        setLoading(false);
        return;
      }

      setItems(returnableItems);
      setStep('items');
    } catch (err: any) {
      handleApiError(err);
    }
    setLoading(false);
  };

  const toggleItem = (productId: string) => {
    setItems(prev => prev.map(item =>
      item.productId === productId ? { ...item, selected: !item.selected } : item
    ));
  };

  const updateReturnQty = (productId: string, qty: number) => {
    setItems(prev => prev.map(item =>
      item.productId === productId
        ? { ...item, returnQuantity: Math.min(Math.max(1, qty), item.maxQuantity) }
        : item
    ));
  };

  const selectAll = () => {
    setItems(prev => prev.map(item => ({ ...item, selected: true })));
  };

  const handleReturn = async () => {
    if (!sale || !user?.company?.id || !user?.branch?.id) return;

    const selectedItems = items.filter(i => i.selected);
    if (selectedItems.length === 0) return;
    if (!reason.trim() || reason.trim().length < 3) return;

    setProcessing(true);
    try {
      const creditNote = await ventasService.returnItems(sale.id, {
        items: selectedItems.map(i => ({
          productId: i.productId,
          quantity: i.returnQuantity,
        })),
        reason: reason.trim(),
      });
      setResult(creditNote);
      setStep('result');
    } catch (err: any) {
      handleApiError(err);
    }
    setProcessing(false);
  };

  const handleClose = () => {
    setStep('search');
    setFolioSearch('');
    setSale(null);
    setItems([]);
    setReason('');
    setResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex bg-black/50 p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl h-auto max-h-[90vh] flex flex-col overflow-hidden mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <RotateCcw size={20} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {step === 'search' && 'Devolución / Nota de Crédito'}
                {step === 'items' && 'Seleccionar productos a devolver'}
                {step === 'result' && 'Devolución completada'}
              </h2>
              <p className="text-xs text-gray-500">
                {step === 'search' && 'Busca la venta por folio para iniciar la devolución'}
                {step === 'items' && `Venta: ${sale?.folio}`}
                {step === 'result' && 'Nota de Crédito generada exitosamente'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <ApiErrorDisplay error={error} errorType={errorType}          onRetry={clearError} onLogin={handleAuthRedirect} />

          {!error && (
            <>
              {/* Step 1: Search for sale */}
              {step === 'search' && (
                <div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">Importante</p>
                      <p className="text-xs text-amber-700 mt-1">
                        La devolución genera una Nota de Crédito que el cliente puede usar en compras futuras.
                        No se realiza reembolso en efectivo ni reversión de tarjeta.
                      </p>
                    </div>
                  </div>

                  <label className="text-sm font-bold text-gray-600 mb-2 block">Folio de la venta</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={folioSearch}
                        onChange={(e) => setFolioSearch(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        placeholder="Ej: V-20260622-0001"
                        className="w-full pl-10 pr-4 h-12 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:ring-4 focus:ring-orange-100 outline-none font-bold text-lg transition-all"
                        autoFocus
                      />
                    </div>
                    <button
                      onClick={handleSearch}
                      disabled={loading || !folioSearch.trim()}
                      className="px-6 h-12 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-bold rounded-xl transition-colors flex items-center gap-2"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                      Buscar
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Select items */}
              {step === 'items' && sale && (
                <div>
                  {/* Sale info summary */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Folio</p>
                      <p className="font-bold text-gray-800">{sale.folio}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Cliente</p>
                      <p className="font-bold text-gray-800">{sale.customer?.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Fecha</p>
                      <p className="text-sm text-gray-700">{new Date(sale.createdAt).toLocaleDateString('es-MX')}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Total venta</p>
                      <p className="font-bold text-gray-800">${Number(sale.grandTotal).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Items list */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Productos ({items.length})
                      </p>
                      <button
                        onClick={selectAll}
                        className="text-xs font-bold text-orange-600 hover:text-orange-700"
                      >
                        Seleccionar todos
                      </button>
                    </div>
                    <div className="space-y-2">
                      {items.map((item) => (
                        <div
                          key={item.productId}
                          className={`border-2 rounded-xl p-3 transition-all cursor-pointer ${
                            item.selected
                              ? 'border-orange-400 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleItem(item.productId)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                item.selected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                              }`}>
                                {item.selected && <Check size={14} className="text-white" />}
                              </div>
                              <div>
                                <p className="font-bold text-sm text-gray-800">{item.name}</p>
                                <p className="text-xs text-gray-500">
                                  Precio: ${item.unitPrice.toFixed(2)} | Original: {item.maxQuantity}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.selected && (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); updateReturnQty(item.productId, item.returnQuantity - 1); }}
                                    className="w-7 h-7 rounded-lg bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    value={item.returnQuantity}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateReturnQty(item.productId, parseInt(e.target.value) || 1)}
                                    min={1}
                                    max={item.maxQuantity}
                                    className="w-14 text-center font-bold text-sm bg-white border border-gray-300 rounded-lg py-1 hide-arrows outline-none"
                                  />
                                  <button
                                    onClick={(e) => { e.stopPropagation(); updateReturnQty(item.productId, item.returnQuantity + 1); }}
                                    className="w-7 h-7 rounded-lg bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                                  >
                                    +
                                  </button>
                                </div>
                              )}
                              <p className="font-bold text-sm text-gray-800 w-20 text-right">
                                ${(item.unitPrice * item.returnQuantity).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-4">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                      Motivo de la devolución
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Describe el motivo de la devolución..."
                      rows={2}
                      className="w-full rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:ring-4 focus:ring-orange-100 outline-none p-3 text-sm resize-none transition-all"
                    />
                  </div>

                  {/* Return summary */}
                  {items.some(i => i.selected) && (
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-white mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold uppercase opacity-80 tracking-wider">
                            Total a devolver ({items.filter(i => i.selected).length} productos)
                          </p>
                          <p className="text-3xl font-black mt-1">
                            ${items.filter(i => i.selected).reduce((sum, i) => sum + (i.unitPrice * i.returnQuantity), 0).toFixed(2)}
                          </p>
                        </div>
                        <RotateCcw size={32} className="opacity-50" />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep('search')}
                      className="flex-1 h-12 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleReturn}
                      disabled={
                        processing ||
                        !items.some(i => i.selected) ||
                        !reason.trim() ||
                        reason.trim().length < 3
                      }
                      className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-bold transition-colors flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <><Loader2 size={18} className="animate-spin" /> Procesando...</>
                      ) : (
                        <><RotateCcw size={18} /> Generar Nota de Crédito</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Result */}
              {step === 'result' && result && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Check size={40} className="text-green-600" />
                  </div>
                  <h3 className="text-xl font-black text-gray-800 mb-2">Nota de Crédito Generada</h3>
                  <p className="text-gray-500 mb-6">La devolución se procesó correctamente</p>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6 text-left">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-bold">Folio Nota de Crédito</p>
                        <p className="font-bold text-gray-800 text-lg tracking-wider">{result.folio}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-bold">Venta original</p>
                        <p className="font-bold text-gray-800">{result.originalSale?.folio || sale?.folio}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-bold">Total devuelto</p>
                        <p className="font-bold text-green-700 text-lg">${Number(result.total).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-bold">Saldo disponible</p>
                        <p className="font-bold text-green-700 text-lg">${Number(result.remaining).toFixed(2)}</p>
                      </div>
                    </div>
                    {result.reason && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <p className="text-[10px] text-gray-500 uppercase font-bold">Motivo</p>
                        <p className="text-sm text-gray-700 mt-0.5">{result.reason}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() =>
                        setCreditNoteToPrint({
                          ...result,
                          folio: result.folio,
                          createdAt: result.createdAt || new Date().toISOString(),
                          items: result.items,
                          originalSale: result.originalSale || { folio: sale?.folio },
                        })
                      }
                      className="flex-1 h-12 rounded-xl bg-white border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Printer size={18} />
                      Imprimir Ticket
                    </button>
                    <button
                      onClick={handleClose}
                      className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold transition-colors"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Fuera de pantalla (no display:none) para que la impresora sí lo vea */}
      <div
        aria-hidden
        style={{ position: 'fixed', left: '-10000px', top: 0, width: '80mm' }}
      >
        {creditNoteToPrint && (
          <CreditNoteTicket creditNote={creditNoteToPrint} originalSale={sale} ref={printRef} />
        )}
      </div>
    </div>
  );
}
